export const ROOT_ID = 'root';

const CONTAINER_PREFIX = 'container::';

const PREVIEW_TRAVEL_PX = 8;

export type PointerDelta = { x: number; y: number };

export const travelledFar = (delta: PointerDelta, since: PointerDelta | null): boolean => {
  if (!since) {
    return true;
  }

  return (
    Math.abs(delta.x - since.x) >= PREVIEW_TRAVEL_PX ||
    Math.abs(delta.y - since.y) >= PREVIEW_TRAVEL_PX
  );
};

export const toContainerId = (groupId: string): string => `${CONTAINER_PREFIX}${groupId}`;
export const isContainerId = (id: string): boolean => id.startsWith(CONTAINER_PREFIX);
export const fromContainerId = (id: string): string => id.slice(CONTAINER_PREFIX.length);

export type LayoutRecord = Record<string, string[]>;

export const sameIdOrder = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((id, index) => id === b[index]);

export const findContainerId = (record: LayoutRecord, id: string): string | undefined => {
  if (id === ROOT_ID || isContainerId(id)) {
    return id;
  }

  return Object.keys(record).find((key) => record[key].includes(id));
};

export const rootRowId = (record: LayoutRecord, id: string): string | undefined => {
  if (isContainerId(id)) {
    return fromContainerId(id);
  }
  if (record[ROOT_ID].includes(id)) {
    return id;
  }
  const owner = findContainerId(record, id);

  return owner && isContainerId(owner) ? fromContainerId(owner) : undefined;
};

export const isRootRow = (record: LayoutRecord, id: string): boolean =>
  record[ROOT_ID].includes(id);

export const moveToRootIndex = (
  record: LayoutRecord,
  activeId: string,
  from: string,
  index: number
): LayoutRecord => {
  const rootItems = [...record[ROOT_ID]];
  rootItems.splice(index, 0, activeId);

  return {
    ...record,
    [from]: record[from].filter((id) => id !== activeId),
    [ROOT_ID]: rootItems,
  };
};

export const everyGroupAtRoot = (record: LayoutRecord): boolean =>
  Object.keys(record)
    .filter(isContainerId)
    .every((key) => record[ROOT_ID].includes(fromContainerId(key)));

export const recordSignature = (record: LayoutRecord): string =>
  Object.keys(record)
    .sort()
    .map((key) => `${key}:${record[key].join(',')}`)
    .join('|');
